<script context="module" lang="ts">
    interface ActionElement {
        readonly tag: 'a' | 'button';
        attributes: Record<string, string>;
    }

    interface LinkActionElement extends ActionElement {
        tag: 'a';
        attributes: { href: string };
    }

    interface ButtonActionElement extends ActionElement {
        tag: 'button';
        attributes: { type: 'button' };
    }

    export interface HeaderLinks {
        title: string;
    }

    const createLinkElement = (url: string): LinkActionElement => ({
        tag: 'a',
        attributes: { href: url },
    });

    const createButtonElement = (): ButtonActionElement => ({
        tag: 'button',
        attributes: { type: 'button' },
    });
</script>

<script lang="ts">
    import { createEventDispatcher } from 'svelte';
    import Chevron from '@amp/web-app-components/assets/icons/chevron-right-bold.svg';
    import PopoverAccessory from '@amp/web-app-components/src/components/Accessory/Popover.svelte';

    type T = $$Generic;
    type PopoverOption = { title?: string } & T;

    const dispatch = createEventDispatcher();

    // New ListenNow headers, fallback for old title/subtitle
    export let headline: string = null;
    export let title: string;
    export let subtitle: string = null;
    export let links: HeaderLinks | null = null;

    export let hasHeadlineLink = false;
    export let hasTitleLink = false;
    export let hasTitlePopover = false;
    export let popoverSelectedOption: PopoverOption = null;
    export let popoverOptions: PopoverOption[] = [];

    // Determine if header exists within Desktop Sidebar or Now Playing Panels
    export let isPanel: boolean = false;

    let titleActionElement: ActionElement;
    $: titleActionElement = links?.title
        ? createLinkElement(links?.title)
        : createButtonElement();
</script>

<div class="header" class:is-panel-header={isPanel}>
    {#if $$slots['artwork']}
        <slot name="artwork" />
    {/if}

    <!--
        <button> expose click actions on these elements to accessibility tools.

        When both a headline and title are present, and the title has a segue
        action but the headline does not, the headline and title are collapsed
        into a single label to provide full context to accessibility tools.

        Example: In Listen Now, there are headings structured like so:
        - headline: "More from"
        - title: {artistName}

        When these are not collapsed, they are announced separately, although
        they are meant to be read together as one unit.

        If there are use cases of heading + title where this assumption doesn't
        hold (in content or i18n), we may need to modify this heuristic.
     -->
    <div class="header-title-wrapper">
        {#if headline}
            <p
                class="headline"
                class:headline-link={hasHeadlineLink}
                aria-hidden={!hasHeadlineLink && hasTitleLink}
                data-testid="header-headline"
                on:click={() => dispatch('headlineClick')}
            >
                {#if hasHeadlineLink}
                    <button type="button">{headline}</button>
                {:else}
                    {headline}
                {/if}
            </p>
        {/if}

        {#if title}
            <!-- svelte-ignore a11y-no-noninteractive-element-interactions -->
            <!-- svelte-ignore a11y-click-events-have-key-events -->
            <h2
                class="title"
                class:title-link={hasTitleLink}
                data-testid="header-title"
                on:click={() => hasTitleLink && dispatch('titleClick')}
            >
                {#if hasTitleLink}
                    <svelte:element
                        this={titleActionElement.tag}
                        {...titleActionElement.attributes}
                        class="title__button"
                        role="link"
                        tabindex="0"
                        aria-label={headline && !hasHeadlineLink
                            ? `${headline} ${title}`
                            : null}
                        on:click|preventDefault
                    >
                        <span class="dir-wrapper" dir="auto">
                            {title}
                        </span>
                        <Chevron class="chevron" aria-hidden="true" />
                    </svelte:element>
                {:else if hasTitlePopover}
                    <span class="dir-wrapper" dir="auto">
                        <PopoverAccessory
                            inheritTypography={true}
                            selectedOption={popoverSelectedOption}
                            options={popoverOptions}
                            on:select={({ detail }) =>
                                dispatch('selectPopoverOption', detail)}
                        >
                            <svelte:fragment slot="label" let:option>
                                {option?.title ?? ''}
                            </svelte:fragment>
                        </PopoverAccessory>
                    </span>
                {:else}
                    <span class="dir-wrapper" dir="auto">
                        {title}
                    </span>
                {/if}
            </h2>
        {/if}

        {#if subtitle}
            <p class="subtitle" data-testid="header-subtitle">
                <span class="dir-wrapper" dir="auto">
                    {subtitle}
                </span>
            </p>
        {/if}
    </div>

    <slot name="buttons" />
</div>

<style lang="scss">
    @use '@amp/web-shared-styles/sasskit-stylekit/ac-sasskit-config';
    @use 'ac-sasskit/core/locale' as *;
    @use '@amp/web-shared-styles/app/core/mixins/margin-gutter' as *;

    .header {
        display: flex;
        align-items: center;
        justify-content: flex-end;

        &:not(.is-panel-header) {
            @include margin-gutter(0, 13px);
        }
    }

    .is-panel-header {
        margin-inline: var(--header-title-margin-inline, 10px);
        padding-top: var(--header-title-padding-top, 15px);
        padding-bottom: 12px;
    }

    .header-title-wrapper {
        flex: 1;
    }

    .header-title-wrapper :global(.chevron) {
        height: var(--header-title-chevron-size, 12px);
        fill: var(--dropdownLightGrayIcon);

        @include rtl {
            transform: scaleX(-1);
        }
    }

    .headline {
        margin-top: 3px;
        color: var(--systemSecondary, #ccc);
        font: var(--title-3);
    }

    .headline-link:hover {
        cursor: pointer;
        text-decoration: underline;
    }

    .title {
        display: inline-block; // confine click area to content width, rather than entire line
        color: var(--header-title-color, var(--systemPrimary, #000));
        font: var(--header-title-font, var(--title-2-emphasized));
    }

    .title__button {
        text-align: start;
    }

    a.title__button {
        text-decoration: none;
    }

    .title-link:hover {
        cursor: pointer;
    }

    .subtitle {
        color: var(--systemSecondary, #ccc);
        font: var(--title-3);
    }

    .header-chevron {
        fill: var(--dropdownLightGrayIcon);
        font: var(--title-2-emphasized);
    }
</style>
